import * as React from "react";
import { useEffect, useRef, useState } from "react";
import "./Dropdown.scss";

export interface DropdownOption {
  name: string;
  value: string;
}

const defaultDropDownOption: DropdownOption = {
  name: i18n["Dropdown.Default.Placeholder"],
  value: "",
};

interface DropdownProps {
  onChange: (newValue: any) => void;
  options: DropdownOption[];
  defaultValue?: DropdownOption;
  additionalClass?: string;
  multipleChoices?: boolean;
}

export default function Dropdown({
  onChange,
  options,
  defaultValue,
  additionalClass,
  multipleChoices,
}: DropdownProps) {
  const getDefaultValue = (): DropdownOption[] => {
    if (defaultValue) return [defaultValue];
    if (multipleChoices) return [];
    return [defaultDropDownOption];
  };
  const [selectedDropdownOption, setSelectedDropdownOption] =
    useState<DropdownOption[]>(getDefaultValue());
  const [isSelectOpen, setIsSelectOpen] = useState<boolean>(false);
  const setSelectState = (value: boolean) => {
    setSelectedIndex(-1);
    setIsSelectOpen(value);
  };
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const wrapperRef = useRef(null);
  const focusBtn = useRef(null);
  const handleClickOutside = (event) => {
    if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
      setSelectState(false);
    }
  };

  const valueChange = (newOption: DropdownOption) => {
    if (!multipleChoices) {
      setSelectState(false);
      if (!isOptionSelected(newOption)) {
        setSelectedDropdownOption([newOption]);
      }
    } else {
      let newOptions = [...selectedDropdownOption];
      isOptionSelected(newOption)
        ? newOptions.splice(newOptions.indexOf(newOption), 1)
        : newOptions.push(newOption);
      setSelectedDropdownOption(newOptions);
    }
    onChange(newOption.value);
  };

  const isOptionSelected = (option: DropdownOption) => {
    return selectedDropdownOption.some((item) =>
      Object.keys(item).every((key) => item[key] === option[key])
    );
  };

  useEffect(() => {
    if (isSelectOpen) {
      focusBtn.current.focus();
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside); // Unbind the event listener on clean up
    };
  }, [wrapperRef, isSelectOpen]);

  const toggleSelect = () => {
    setSelectState(!isSelectOpen);
  };

  const openSelect = () => {
    if (!isSelectOpen) {
      setSelectState(true);
    }
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    const isTab = event.key === "Tab";
    if (isTab && !isSelectOpen) {
      return;
    } else {
      event.preventDefault();
      if (
        (event.key === "ArrowDown" || event.key === "ArrowRight" || isTab) &&
        selectedIndex < options.length - 1
      ) {
        setSelectedIndex(selectedIndex + 1);
      }
      if ((event.key === "ArrowUp" || event.key === "ArrowLeft") && selectedIndex > 0) {
        setSelectedIndex(selectedIndex - 1);
      }
      if (event.key === "Enter") {
        if (!isSelectOpen) {
          setSelectState(true);
        } else {
          if (selectedIndex === -1) {
            setSelectState(false);
          } else {
            valueChange(options[selectedIndex]);
          }
        }
      }
    }
  };

  return (
    <div
      ref={wrapperRef}
      className={`dropdown-container ${
        additionalClass ? additionalClass : ""
      } ${isSelectOpen ? "active" : ""}`}
    >
      <button
        ref={focusBtn}
        className="focus-button"
        onFocus={() => openSelect()}
        onKeyDown={(event) => onKeyDown(event)}
      ></button>
      <div className="selected-option" onClick={() => toggleSelect()}>
        {multipleChoices ? "+" : selectedDropdownOption[0]?.name}
      </div>
      <div className="option-container">
        {options.map((option: DropdownOption, index: number) => (
          <div
            key={option.value}
            className={`option ${isOptionSelected(option) ? "selected" : ""} ${
              selectedIndex === index ? "hover" : ""
            }`}
            onClick={() => valueChange(option)}
            onKeyDown={() => valueChange(option)}
          >
            {!multipleChoices && (
              <input type="radio" checked={isOptionSelected(option)} readOnly name={option.value} />
            )}

            {multipleChoices && (
              <input
                type="checkbox"
                checked={isOptionSelected(option)}
                readOnly
                name={option.value}
              />
            )}
            <span>{option.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
